import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { X } from "lucide-react";
import { SheetTitle } from "@/components/ui/sheet";
import type { QuoteRow } from "@/hooks/useQuotes";
import { formatEventStartTime, SERVICE_TYPE_SHORT_LABELS, effectiveServiceType, type ServiceType } from "@/types/quotes";
import { guestCountLabel, paymentPill, statusPill, type MenuEntry, type MenuEntryId, type NextStep, type NextStepId, type PaymentSummary } from "./drawerLogic";
import { InlineConfirm } from "./InlineConfirm";
import { NextStepBox } from "./NextStepBox";
import { Pill } from "./Pill";
import { QuoteActionsMenu } from "./QuoteActionsMenu";
import { UnsavedChangesBar } from "./UnsavedChangesBar";

export interface CloseAsk {
  onKeep: () => void;
  onDiscard: () => void;
  onSave: () => void;
  saving?: boolean;
}

export interface DeleteAsk {
  text: string;
  onKeep: () => void;
  onDelete: () => void;
  busy?: boolean;
}

interface QuoteDrawerHeaderProps {
  quote: QuoteRow;
  summary: PaymentSummary;
  /** getDocumentLabel's word, shown as ghost text after the pills. */
  docLabel: string;
  /** The event's number for "Part of …"; null while it loads or with no event. */
  eventNumber: string | null;
  menu: { entries: (MenuEntry | "sep")[]; onSelect: (id: MenuEntryId) => void; open: boolean; onOpenChange: (o: boolean) => void; busy?: Partial<Record<MenuEntryId, boolean>> };
  onClose: () => void;
  /** "Open event →": the drawer decides whether leaving is allowed. */
  onOpenEvent: (e: MouseEvent<HTMLAnchorElement>) => void;
  onClientClick?: () => void;
  closeAsk: CloseAsk | null;
  deleteAsk: DeleteAsk | null;
  nextStep: NextStep;
  onNextStep: (id: NextStepId) => void;
  nextStepBusy?: Partial<Record<NextStepId, boolean>>;
}

/** "Sat, Nov 28" from a yyyy-mm-dd date. */
function stripDate(value: string | null): string {
  const d = value ? parseISO(value) : null;
  return d && !Number.isNaN(d.getTime()) ? format(d, "EEE, MMM d") : "";
}

function StripSep() {
  return <span aria-hidden className="text-border">|</span>;
}

/** The event strip: client, date · time, guests, service, and Open event → (#1559). */
function EventStrip({ quote, eventNumber, onOpenEvent, onClientClick }: Pick<QuoteDrawerHeaderProps, "quote" | "eventNumber" | "onOpenEvent" | "onClientClick">) {
  const when = [stripDate(quote.event_date), formatEventStartTime(quote.event_start_time)].filter(Boolean).join(" · ");
  const svc = effectiveServiceType(quote.service_type, quote.fulfillment_type) as ServiceType;
  const client: ReactNode = quote.client_name || "No client";
  return (
    <div data-event-strip className="flex flex-wrap items-center gap-x-3.5 gap-y-1 rounded-lg bg-muted px-3 py-2 text-[13px] tabular-nums">
      {quote.event_id && eventNumber && (
        <>
          <span>Part of <b className="font-semibold">{eventNumber}</b></span>
          <StripSep />
        </>
      )}
      {onClientClick && quote.contact_id ? (
        <button type="button" onClick={onClientClick} className="font-semibold hover:underline">{client}</button>
      ) : (
        <b className="font-semibold">{client}</b>
      )}
      {when && <span>{when}</span>}
      <span>{guestCountLabel(quote.guest_count)}</span>
      <span>{SERVICE_TYPE_SHORT_LABELS[svc] ?? quote.service_type}</span>
      {quote.event_id && (
        <Link to={`/admin/events/${quote.event_id}`} onClick={onOpenEvent} className="text-[13px] font-bold text-accent hover:underline">
          Open event →
        </Link>
      )}
    </div>
  );
}

/** The drawer's header: title row, event strip, the asks and Next step (#1559). */
export function QuoteDrawerHeader(props: QuoteDrawerHeaderProps) {
  const { quote, summary, docLabel, menu, onClose, closeAsk, deleteAsk, nextStep, onNextStep, nextStepBusy } = props;
  const status = statusPill(quote.status);
  const pay = paymentPill(quote.status, summary.paid, Number(quote.total) || 0);
  return (
    <div className="grid gap-2.5 border-b bg-card px-5 pb-3 pt-3.5 max-[760px]:px-4">
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="font-display text-[21px] tabular-nums">{quote.quote_number}</SheetTitle>
          <Pill tone={status.tone}>{status.label}</Pill>
          {pay && <Pill tone={pay.tone}>{pay.label}</Pill>}
          {quote.square_source && <Pill tone="info">From Square</Pill>}
          {quote.is_lost && <Pill tone="hot">Event lost</Pill>}
          <span className="text-[12.5px] text-muted-foreground">{docLabel}</span>
        </div>
        <div className="flex gap-1.5">
          <QuoteActionsMenu {...menu} />
          <button
            type="button"
            aria-label="Close quote"
            onClick={onClose}
            className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[7px] text-muted-foreground hover:bg-muted"
          >
            <X className="h-[17px] w-[17px]" />
          </button>
        </div>
      </div>
      <EventStrip {...props} />
      {closeAsk && <UnsavedChangesBar {...closeAsk} />}
      {deleteAsk && (
        <InlineConfirm
          tone="hot"
          text={deleteAsk.text}
          cancelLabel="Keep it"
          confirmLabel="Delete"
          onCancel={deleteAsk.onKeep}
          onConfirm={deleteAsk.onDelete}
          busy={deleteAsk.busy}
        />
      )}
      <NextStepBox step={nextStep} onAction={onNextStep} busy={nextStepBusy} />
    </div>
  );
}
